/**
 * Executive Reports Dashboard
 * Jellyfish/BluOptima-style engineering intelligence
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card, Badge, Select, Tabs, Progress, Alert } from 'flowbite-react';
import {
  HiChartBar,
  HiShieldCheck,
  HiLightningBolt,
  HiExclamation,
  HiTrendingUp,
  HiTrendingDown,
  HiUserGroup,
  HiCode,
  HiClock,
  HiRefresh,
} from 'react-icons/hi';
import {
  getEngineeringInvestment,
  getStandardsRoi,
  getAiLeverage,
  getRiskForecast,
  EngineeringInvestmentReport,
  StandardsRoiReport,
  AiLeverageReport,
  RiskForecastReport,
  ReportPeriod,
} from '../api/reports';
import { useStatus } from '../contexts/StatusContext';

const ExecutiveReports: React.FC = () => {
  const { hasFeature } = useStatus();
  const latestRequest = useRef(0);
  const [period, setPeriod] = useState<ReportPeriod>('30d');
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Report data
  const [investmentData, setInvestmentData] = useState<EngineeringInvestmentReport | null>(null);
  const [roiData, setRoiData] = useState<StandardsRoiReport | null>(null);
  const [aiData, setAiData] = useState<AiLeverageReport | null>(null);
  const [riskData, setRiskData] = useState<RiskForecastReport | null>(null);

  const loadReports = async () => {
    const request = ++latestRequest.current;
    setLoading(true);
    setError(null);
    setInvestmentData(null);
    setRoiData(null);
    setAiData(null);
    setRiskData(null);

    try {
      const [investment, roi, ai, risk] = await Promise.all([
        getEngineeringInvestment(period),
        getStandardsRoi(period),
        getAiLeverage(period),
        getRiskForecast(),
      ]);

      if (request !== latestRequest.current) return;

      if (investment.success) setInvestmentData(investment.data || null);
      if (roi.success) setRoiData(roi.data || null);
      if (ai.success) setAiData(ai.data || null);
      if (risk.success) setRiskData(risk.data || null);

      const sources = [
        ['Engineering investment', investment],
        ['Standards ROI', roi],
        ['AI leverage', ai],
        ['Risk forecast', risk],
      ] as const;
      const errors = sources
        .filter(([, result]) => !result.success || !result.data)
        .map(([name, result]) => `${name}: ${result.error || 'No data returned'}`);
      if (errors.length > 0) setError(errors.join('; '));
    } catch (err) {
      if (request !== latestRequest.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load reports');
    } finally {
      if (request === latestRequest.current) setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, [period]);

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'critical': return 'failure';
      case 'high': return 'warning';
      case 'elevated': return 'warning';
      case 'moderate': return 'info';
      default: return 'success';
    }
  };

  const getRiskBgColor = (level: string) => {
    switch (level) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'elevated': return 'bg-yellow-500';
      case 'moderate': return 'bg-blue-500';
      default: return 'bg-green-500';
    }
  };

  // Feature gate — Professional+ only
  if (!hasFeature('analytics')) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96">
        <HiChartBar className="h-16 w-16 text-gray-300 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Professional Feature</h2>
        <p className="text-gray-500 text-center max-w-md">
          Executive Reports are available on Professional and Enterprise plans.
          Upgrade to access engineering investment analysis, standards ROI, and risk forecasting.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Executive Reports</h1>
          <p className="mt-1 text-sm text-gray-500">
            Engineering intelligence and team health metrics
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <Select
            value={period}
            onChange={(e) => setPeriod(e.target.value as ReportPeriod)}
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </Select>
          <button
            aria-label="Refresh reports"
            onClick={loadReports}
            disabled={loading}
            className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
          >
            <HiRefresh className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <Alert color="warning">
          {error}
        </Alert>
      )}

      {/* Risk Score Banner */}
      {riskData?.risk_level && (
        <Card>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className={`p-4 rounded-full ${getRiskBgColor(riskData.risk_level)}`}>
                <HiExclamation className="h-8 w-8 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Overall Risk Score</p>
                <p className="text-3xl font-bold text-gray-900">{riskData.overall_risk_score ?? 0}/100</p>
              </div>
            </div>
            <Badge color={getRiskColor(riskData.risk_level)} size="xl">
              {riskData.risk_level.toUpperCase()}
            </Badge>
          </div>
          {riskData.summary && (
          <div className="mt-4 grid grid-cols-6 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-gray-900">{riskData.summary.inactive_developers ?? 0}</p>
              <p className="text-xs text-gray-500">Inactive Devs</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{riskData.summary.high_velocity_devs ?? 0}</p>
              <p className="text-xs text-gray-500">Burnout Risk</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{riskData.summary.critical_silos ?? 0}</p>
              <p className="text-xs text-gray-500">Critical Silos</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{riskData.summary.stale_prs ?? 0}</p>
              <p className="text-xs text-gray-500">Stale PRs</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{riskData.summary.active_alerts ?? 0}</p>
              <p className="text-xs text-gray-500">Active Alerts</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{riskData.summary.high_churn_files ?? 0}</p>
              <p className="text-xs text-gray-500">High Churn</p>
            </div>
          </div>
          )}
        </Card>
      )}

      {/* Tabs */}
      <Tabs
        aria-label="Report tabs"
        onActiveTabChange={setActiveTab}
      >
        {/* Engineering Investment Tab */}
        <Tabs.Item
          active={activeTab === 0}
          title="Engineering Investment"
          icon={HiChartBar}
        >
          {loading ? (
            <LoadingState />
          ) : investmentData?.summary ? (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-4">
                <MetricCard
                  icon={HiCode}
                  label="Total Commits"
                  value={(investmentData.summary.total_commits ?? 0).toLocaleString()}
                  color="blue"
                />
                <MetricCard
                  icon={HiUserGroup}
                  label="Contributors"
                  value={(investmentData.summary.total_contributors ?? 0).toString()}
                  color="green"
                />
                <MetricCard
                  icon={HiChartBar}
                  label="Repositories"
                  value={(investmentData.summary.total_repos ?? 0).toString()}
                  color="purple"
                />
                <MetricCard
                  icon={HiTrendingUp}
                  label="Lines Changed"
                  value={(investmentData.summary.total_lines_changed ?? 0).toLocaleString()}
                  color="amber"
                />
              </div>

              {/* Investment by Module */}
              <Card>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Investment by Module</h3>
                <div className="space-y-3">
                  {(investmentData.investment_by_module || []).slice(0, 10).map((module, idx) => (
                    <div key={idx} className="flex items-center space-x-4">
                      <div className="w-32 text-sm font-medium text-gray-700 truncate">
                        {module.module}
                      </div>
                      <div className="flex-1">
                        <Progress
                          progress={parseFloat(module.commit_percentage.toString())}
                          color="blue"
                          size="lg"
                        />
                      </div>
                      <div className="w-20 text-right text-sm text-gray-500">
                        {module.commit_count} commits
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Top Contributors */}
              <Card>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Contributors</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-gray-700 bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left">Developer</th>
                        <th className="px-4 py-2 text-right">Commits</th>
                        <th className="px-4 py-2 text-right">Lines Added</th>
                        <th className="px-4 py-2 text-right">PRs Merged</th>
                        <th className="px-4 py-2 text-right">Compliance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(investmentData.top_contributors || []).map((dev, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{dev.developer_name || 'Unknown'}</div>
                            <div className="text-xs text-gray-500">{dev.developer_email}</div>
                          </td>
                          <td className="px-4 py-3 text-right">{dev.total_commits}</td>
                          <td className="px-4 py-3 text-right text-green-600">+{dev.total_lines_added?.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right">{dev.total_prs_merged}</td>
                          <td className="px-4 py-3 text-right">
                            <Badge color={parseFloat(dev.avg_compliance?.toString() || '0') >= 70 ? 'success' : 'warning'}>
                              {dev.avg_compliance?.toFixed(0) || 'N/A'}%
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          ) : (
            <EmptyState message="No engineering data available" />
          )}
        </Tabs.Item>

        {/* Standards ROI Tab */}
        <Tabs.Item
          active={activeTab === 1}
          title="Standards ROI"
          icon={HiShieldCheck}
        >
          {loading ? (
            <LoadingState />
          ) : roiData?.summary ? (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-4">
                <MetricCard
                  icon={HiShieldCheck}
                  label="Compliance Score"
                  value={`${roiData.summary.avg_compliance_score ?? 0}%`}
                  color="green"
                />
                <MetricCard
                  icon={HiTrendingUp}
                  label="Compliance Rate"
                  value={`${roiData.summary.compliance_rate ?? 0}%`}
                  color="blue"
                />
                <MetricCard
                  icon={HiExclamation}
                  label="Anti-Pattern Rate"
                  value={`${roiData.summary.anti_pattern_rate ?? 0}%`}
                  color="red"
                />
                <MetricCard
                  icon={HiLightningBolt}
                  label="Standards Shown"
                  value={(roiData.summary.standards_shown ?? 0).toLocaleString()}
                  color="purple"
                />
              </div>

              {/* Insights */}
              {(roiData.insights?.length ?? 0) > 0 && (
                <div className="space-y-2">
                  {roiData.insights.map((insight, idx) => (
                    <Alert
                      key={idx}
                      color={insight.type === 'positive' ? 'success' : insight.type === 'warning' ? 'failure' : 'warning'}
                    >
                      {insight.message}
                      {insight.developers && (
                        <span className="ml-2 text-sm">({insight.developers.join(', ')})</span>
                      )}
                    </Alert>
                  ))}
                </div>
              )}

              {/* Compliance by Developer */}
              <Card>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Compliance by Developer</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-gray-700 bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left">Developer</th>
                        <th className="px-4 py-2 text-right">Commits</th>
                        <th className="px-4 py-2 text-right">Compliant</th>
                        <th className="px-4 py-2 text-right">Anti-Patterns</th>
                        <th className="px-4 py-2 text-right">Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(roiData.compliance_by_developer || []).map((dev, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{dev.developer_name || 'Unknown'}</div>
                            <div className="text-xs text-gray-500">{dev.developer_email}</div>
                          </td>
                          <td className="px-4 py-3 text-right">{dev.total_commits}</td>
                          <td className="px-4 py-3 text-right text-green-600">{dev.compliant_commits}</td>
                          <td className="px-4 py-3 text-right text-red-600">{dev.anti_pattern_commits}</td>
                          <td className="px-4 py-3 text-right">
                            <ComplianceBadge score={dev.avg_compliance_score} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>

              {/* Anti-Patterns Detected */}
              {(roiData.anti_patterns_detected?.length ?? 0) > 0 && (
                <Card>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Anti-Patterns Detected</h3>
                  <div className="space-y-2">
                    {roiData.anti_patterns_detected.map((ap, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <HiExclamation className="h-5 w-5 text-red-500" />
                          <span className="font-medium text-gray-900">{ap.pattern_name}</span>
                          <Badge color={ap.severity === 'error' ? 'failure' : 'warning'}>
                            {ap.severity}
                          </Badge>
                        </div>
                        <div className="text-sm text-gray-500">
                          {ap.occurrences} occurrences in {ap.repos_affected} repos
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          ) : (
            <EmptyState message="No standards data available" />
          )}
        </Tabs.Item>

        {/* AI Leverage Tab */}
        <Tabs.Item
          active={activeTab === 2}
          title="AI Leverage"
          icon={HiLightningBolt}
        >
          {loading ? (
            <LoadingState />
          ) : aiData?.summary ? (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-4">
                <MetricCard
                  icon={HiUserGroup}
                  label="AI Users"
                  value={(aiData.summary.unique_ai_users ?? 0).toString()}
                  color="purple"
                />
                <MetricCard
                  icon={HiClock}
                  label="Avg Session"
                  value={`${aiData.summary.avg_session_minutes ?? 0} min`}
                  color="blue"
                />
                <MetricCard
                  icon={HiCode}
                  label="AI Commits"
                  value={`${aiData.summary.ai_commit_percentage ?? 0}%`}
                  color="green"
                />
                <MetricCard
                  icon={HiTrendingUp}
                  label="Productivity"
                  value={`${aiData.summary.productivity_multiplier ?? 0}x`}
                  color="amber"
                />
              </div>

              {/* Insights */}
              {(aiData.insights?.length ?? 0) > 0 && (
                <div className="space-y-2">
                  {aiData.insights.map((insight, idx) => (
                    <Alert
                      key={idx}
                      color={insight.type === 'positive' ? 'success' : 'info'}
                    >
                      {insight.message}
                    </Alert>
                  ))}
                </div>
              )}

              {/* AI vs Non-AI Comparison */}
              {aiData.comparison && (
              <Card>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">AI vs Non-AI Comparison</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h4 className="font-semibold text-purple-900 mb-3 flex items-center">
                      <HiLightningBolt className="h-5 w-5 mr-2" />
                      AI-Assisted Developers
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Developers</span>
                        <span className="font-medium">{aiData.comparison.ai_assisted?.developer_count ?? 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Avg Commits/Dev</span>
                        <span className="font-medium">{aiData.comparison.ai_assisted?.avg_commits_per_dev ?? 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Avg Lines/Dev</span>
                        <span className="font-medium">{aiData.comparison.ai_assisted?.avg_lines_per_dev ?? 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Avg Compliance</span>
                        <span className="font-medium">{aiData.comparison.ai_assisted?.avg_compliance ?? 0}%</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <HiUserGroup className="h-5 w-5 mr-2" />
                      Non-AI Developers
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Developers</span>
                        <span className="font-medium">{aiData.comparison.non_ai?.developer_count ?? 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Avg Commits/Dev</span>
                        <span className="font-medium">{aiData.comparison.non_ai?.avg_commits_per_dev ?? 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Avg Lines/Dev</span>
                        <span className="font-medium">{aiData.comparison.non_ai?.avg_lines_per_dev ?? 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Avg Compliance</span>
                        <span className="font-medium">{aiData.comparison.non_ai?.avg_compliance ?? 0}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
              )}
            </div>
          ) : (
            <EmptyState message="No AI leverage data available" />
          )}
        </Tabs.Item>

        {/* Risk Forecast Tab */}
        <Tabs.Item
          active={activeTab === 3}
          title="Risk Forecast"
          icon={HiExclamation}
        >
          {loading ? (
            <LoadingState />
          ) : riskData?.risks ? (
            <div className="space-y-6">
              {/* Recommendations */}
              {(riskData.recommendations?.length ?? 0) > 0 && (
                <Card>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Recommendations</h3>
                  <div className="space-y-3">
                    {riskData.recommendations.map((rec, idx) => (
                      <div
                        key={idx}
                        className={`p-4 rounded-lg border-l-4 ${
                          rec.priority === 'critical' ? 'bg-red-50 border-red-500' :
                          rec.priority === 'high' ? 'bg-orange-50 border-orange-500' :
                          rec.priority === 'medium' ? 'bg-yellow-50 border-yellow-500' :
                          'bg-blue-50 border-blue-500'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-gray-900">{rec.action}</span>
                          <Badge color={
                            rec.priority === 'critical' ? 'failure' :
                            rec.priority === 'high' ? 'warning' :
                            'info'
                          }>
                            {rec.priority}
                          </Badge>
                        </div>
                        {rec.details && (
                          <p className="text-sm text-gray-600 mb-2">{rec.details}</p>
                        )}
                        {rec.affected && rec.affected.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {rec.affected.slice(0, 5).map((item, i) => (
                              <Badge key={i} color="gray" size="sm">{item}</Badge>
                            ))}
                            {rec.affected.length > 5 && (
                              <Badge color="gray" size="sm">+{rec.affected.length - 5} more</Badge>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Knowledge Silos */}
              {(riskData.risks.knowledge_silos?.length ?? 0) > 0 && (
                <Card>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Knowledge Silos (Bus Factor Risk)</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-gray-700 bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left">Module</th>
                          <th className="px-4 py-2 text-left">Primary Owner</th>
                          <th className="px-4 py-2 text-center">Contributors</th>
                          <th className="px-4 py-2 text-center">Risk</th>
                        </tr>
                      </thead>
                      <tbody>
                        {riskData.risks.knowledge_silos.map((silo, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900">{silo.module_path}</div>
                              <div className="text-xs text-gray-500">{silo.repo_name}</div>
                            </td>
                            <td className="px-4 py-3">{silo.primary_contributor}</td>
                            <td className="px-4 py-3 text-center">{silo.total_contributors}</td>
                            <td className="px-4 py-3 text-center">
                              <Badge color={silo.risk_level === 'critical' ? 'failure' : 'warning'}>
                                {silo.risk_level}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}

              {/* Inactive Developers */}
              {(riskData.risks.inactive_developers?.length ?? 0) > 0 && (
                <Card>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Inactive Developers</h3>
                  <div className="space-y-2">
                    {riskData.risks.inactive_developers.map((dev, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                        <div>
                          <span className="font-medium text-gray-900">{dev.name || dev.email}</span>
                          <span className="ml-2 text-sm text-gray-500">
                            Last commit: {new Date(dev.last_commit).toLocaleDateString()}
                          </span>
                        </div>
                        <Badge color={dev.days_inactive > 10 ? 'failure' : 'warning'}>
                          {dev.days_inactive} days inactive
                        </Badge>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Burnout Risk */}
              {(riskData.risks.burnout_risk?.length ?? 0) > 0 && (
                <Card>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Potential Burnout Risk</h3>
                  <div className="space-y-2">
                    {riskData.risks.burnout_risk.map((dev, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                        <div>
                          <span className="font-medium text-gray-900">{dev.name || dev.email}</span>
                        </div>
                        <div className="flex items-center space-x-4 text-sm">
                          <span>{dev.commits_7d} commits/week</span>
                          <span>{dev.weekend_commits} weekend</span>
                          <span>{dev.after_hours} after-hours</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          ) : (
            <EmptyState message="No risk data available" />
          )}
        </Tabs.Item>
      </Tabs>
    </div>
  );
};

// Helper Components
const MetricCard: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  color: 'blue' | 'green' | 'purple' | 'amber' | 'red';
}> = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    amber: 'bg-amber-100 text-amber-600',
    red: 'bg-red-100 text-red-600',
  };

  return (
    <Card>
      <div className="flex items-center">
        <div className={`p-3 rounded-full ${colorClasses[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </Card>
  );
};

const ComplianceBadge: React.FC<{ score: number }> = ({ score }) => {
  const scoreNum = parseFloat(score?.toString() || '0');
  let color: 'success' | 'warning' | 'failure' = 'success';
  if (scoreNum < 50) color = 'failure';
  else if (scoreNum < 70) color = 'warning';

  return (
    <Badge color={color}>
      {scoreNum.toFixed(0)}%
    </Badge>
  );
};

const LoadingState: React.FC = () => (
  <div className="flex items-center justify-center py-12">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mindmeld-primary"></div>
  </div>
);

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="text-center py-12">
    <HiChartBar className="mx-auto h-12 w-12 text-gray-400" />
    <h3 className="mt-2 text-sm font-medium text-gray-900">{message}</h3>
    <p className="mt-1 text-sm text-gray-500">
      Connect repositories and enable tracking to see reports.
    </p>
  </div>
);

export default ExecutiveReports;
